import {useMediaQueries} from 'presentation/pages/private/hooks';
import {useRef, useState} from 'react';
import type {TherapySessionsRef} from '../components/TherapySessions/types';
import {translations} from 'presentation/translations-new';
import {useRefundRequest} from 'presentation/pages/private/refund/hooks';
import {useFormManagement} from './useFormManagement';
import {useTherapies} from '../../../hooks/useTherapies';
import {useStepStore} from '../../../../../store/useStepStore';
import {useSubmitStatusStore} from '../../../../../store/useSubmitStatusStore';
import type {ErrorState} from '../../TherapyList/types';
import {SubmitStatusEnum} from '../../../../../types';
import {useDeleteBatchProcedureMutation} from '../../../queries/useDeleteBatchProcedureMutation';
import type {DeleteBatchRefundProcedureParams} from 'domain/usecases';

const {removeModal} = translations['pt-br'].privateFlow.refund.therapies;

export const useAddTherapy = () => {
	const {isTablet} = useMediaQueries();
	const therapySessionsRef = useRef<TherapySessionsRef>(null);

	const {requests} = useRefundRequest();
	const {therapyDataMethods} = useFormManagement();
	const watchedTherapyForm = therapyDataMethods.watch();
	const {isEditPageMode, isEmptyTherapies, handleGoToTherapyMode} = useTherapies();
	const previousStep = useStepStore(state => state.previousStep);
	const setSubmitStatus = useSubmitStatusStore(state => state.setStatus);

	const [openGoBackModal, setOpenGoBackModal] = useState({
		open: false,
		goToList: false,
		deleteProcedure: false,
	});
	const [errorState, setErrorState] = useState<ErrorState | null>(null);

	const handleGoBack = (isList?: boolean) => {
		if (isList) {
			handleGoToTherapyMode('list');
			return;
		}
		setSubmitStatus(SubmitStatusEnum.UPDATE_REQUEST);
		previousStep();
	};

	const handleOpenGoBackModal = () => {
		const isExistsDataSessions = therapySessionsRef.current?.watchedSessions.every(
			session =>
				!!session.serviceDate ||
				(!!session.sessionValue && session.sessionValue !== '0,00'),
		);
		const isExistsDataSessionsSaved = watchedTherapyForm.sessions.some(
			session => session.isSaved,
		);
		const isTherapyFormEmpty = !watchedTherapyForm.therapyType && !isExistsDataSessions;

		if (isEmptyTherapies && isTherapyFormEmpty) {
			handleGoBack();
		}
		if (isEmptyTherapies && !isTherapyFormEmpty && !isExistsDataSessionsSaved) {
			setOpenGoBackModal({
				open: true,
				goToList: false,
				deleteProcedure: false,
			});
		}
		if (isEmptyTherapies && !isTherapyFormEmpty && isExistsDataSessionsSaved) {
			setOpenGoBackModal({
				open: true,
				goToList: false,
				deleteProcedure: true,
			});
		}

		if (!isEmptyTherapies && isTherapyFormEmpty) {
			handleGoBack(true);
		}
		if (!isEmptyTherapies && !isTherapyFormEmpty && !isExistsDataSessionsSaved) {
			setOpenGoBackModal({
				open: true,
				goToList: true,
				deleteProcedure: false,
			});
		}
		if (!isEmptyTherapies && !isTherapyFormEmpty && isExistsDataSessionsSaved) {
			setOpenGoBackModal({
				open: true,
				goToList: true,
				deleteProcedure: true,
			});
		}
	};

	const handleCloseQuitModal = () => {
		setOpenGoBackModal({
			open: false,
			goToList: false,
			deleteProcedure: false,
		});
	};

	const {mutateAsync} = useDeleteBatchProcedureMutation();
	const handleRemoveTherapy = async () => {
		const params: DeleteBatchRefundProcedureParams = {
			protocolCode: requests?.protocolCode?.toString() || '',
			requestCode: requests?.requestData[0]?.requestCode?.toString() || '',
			procedureCode: watchedTherapyForm.therapyType?.code.toString() || '',
		};
		await mutateAsync(params, {
			onSuccess: () => {
				handleCloseQuitModal();
				handleGoBack(openGoBackModal.goToList);
			},
			onError: () => {
				setErrorState({
					type: 'deleteBatchProcedure',
					message: removeModal.errorMessage,
				});
				handleCloseQuitModal();
			},
		});
	};

	const handleBackToList = async () => {
		if (openGoBackModal.deleteProcedure) {
			await handleRemoveTherapy();
			return;
		}
		handleCloseQuitModal();
		handleGoBack(openGoBackModal.goToList);
	};
	return {
		isEditPageMode,
		handleOpenGoBackModal,
		isTablet,
		therapyDataMethods,
		therapySessionsRef,
		handleCloseQuitModal,
		handleBackToList,
		openGoBackModal,
		setErrorState,
		errorState,
	};
};
